'use client';

import { useEffect, useState } from 'react';

import { API_BASE_URL } from '@/lib/api';

/** Where the last known answer is kept, so a hidden page stays hidden on the next mount. */
const REMEMBERED = 'nav:all-leaders-hidden';

/**
 * Whether the All Leaders page is published, per its CMS record.
 *
 * Lives in a hook because the header and the footer both link to that page and
 * both have to drop the link together - one of them remembering to check and
 * the other not is exactly how a "hidden" page stays reachable.
 *
 * Returns true until told otherwise. An editor who published a page should not
 * have it vanish because a request failed, and the page is visible by default,
 * so an optimistic answer is the correct one when there is no answer yet.
 */
export function useAllLeadersVisible(): boolean {
  /*
   * Starts true so the server-rendered markup and the first client render
   * agree. Seeding from storage instead would differ from what the server
   * sent and trip a hydration mismatch.
   */
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    /*
     * The stored answer lands in the same tick as mount, so it is not visible
     * as a flash. Without it, the link would render and then disappear on
     * every page load of a site that has this switched off - the header
     * mounts fresh on all 33 pages that use it.
     */
    try {
      if (window.localStorage.getItem(REMEMBERED) === '1') setVisible(false);
    } catch {
      // Private browsing, or storage disabled. The fetch still corrects it.
    }

    let cancelled = false;
    fetch(`${API_BASE_URL}/api/content/all-leaders-banner/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        // No banner record, or an older API without the field, both mean visible.
        const published = data.is_visible !== false;
        setVisible(published);
        try {
          if (published) window.localStorage.removeItem(REMEMBERED);
          else window.localStorage.setItem(REMEMBERED, '1');
        } catch {
          // Nothing to remember it with; the next mount re-fetches anyway.
        }
      })
      .catch(() => {
        // Keep whatever we have rather than hiding a published page.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return visible;
}

export default useAllLeadersVisible;
